import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";
import { useCart, useLang } from "../store";
import { useT } from "../i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function CheckoutSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const { lang } = useLang();
  const t = useT(lang);
  const navigate = useNavigate();
  const { clearCart } = useCart();

  // Derive initial state synchronously to avoid setState-in-effect.
  const [state, setState] = useState(() =>
    sessionId ? { kind: "checking" } : { kind: "failed", message: "" }
  );
  const attempts = useRef(0);

  // Keep latest t/clearCart accessible inside the effect without re-running it.
  const tRef = useRef(t);
  const clearCartRef = useRef(clearCart);
  useEffect(() => { tRef.current = t; clearCartRef.current = clearCart; });

  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;

    const poll = async () => {
      if (cancelled) return;
      try {
        const res = await axios.get(`${API}/checkout/status/${sessionId}`);
        const { payment_status, status } = res.data;
        if (cancelled) return;
        if (payment_status === "paid") {
          setState({ kind: "success" });
          clearCartRef.current();
          return;
        }
        if (status === "expired") {
          setState({ kind: "failed", message: tRef.current.checkout.failedSub });
          return;
        }
      } catch (err) {
        console.error("Checkout status poll failed:", err);
      }
      attempts.current += 1;
      if (attempts.current >= 8) {
        if (!cancelled) setState({ kind: "timeout", message: tRef.current.checkout.timeout });
        return;
      }
      setTimeout(poll, 2000);
    };

    poll();
    return () => { cancelled = true; };
  }, [sessionId]);

  return (
    <section className="min-h-[70vh] flex items-center justify-center px-5 py-20">
      <div className="max-w-lg w-full bg-white border-[3px] border-[#0C0A09] shadow-[8px_8px_0_0_#0C0A09] p-8 md:p-12 text-center">
        {state.kind === "checking" && (
          <>
            <Loader2 size={48} strokeWidth={2.5} className="mx-auto animate-spin" />
            <h1 className="font-display text-4xl md:text-5xl mt-4">{t.checkout.preparing}</h1>
            <p className="mt-3 font-medium opacity-80" data-testid="checkout-status-msg">{t.checkout.checking}</p>
          </>
        )}
        {state.kind === "success" && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#9BE800] border-[3px] border-[#0C0A09] flex items-center justify-center mx-auto">
              <CheckCircle2 size={36} strokeWidth={2.75} />
            </div>
            <h1 data-testid="checkout-success-title" className="font-display text-5xl md:text-6xl mt-5 leading-none">{t.checkout.successTitle}</h1>
            <p className="mt-4 font-medium opacity-80">{t.checkout.successSub}</p>
            <button data-testid="checkout-back-home-btn" onClick={() => navigate("/")} className="nb-btn mt-7">{t.checkout.successCta}</button>
          </>
        )}
        {(state.kind === "failed" || state.kind === "timeout") && (
          <>
            <div className="w-20 h-20 rounded-full bg-[#FF3D7F] border-[3px] border-[#0C0A09] flex items-center justify-center mx-auto">
              <XCircle size={36} strokeWidth={2.75} />
            </div>
            <h1 className="font-display text-4xl md:text-5xl mt-5 leading-none">{t.checkout.failedTitle}</h1>
            <p className="mt-3 font-medium opacity-80">{state.message || t.checkout.failedSub}</p>
            <button onClick={() => navigate("/")} className="nb-btn white mt-7">← {t.checkout.successCta}</button>
          </>
        )}
      </div>
    </section>
  );
}
